/* Image loader — every photo on the page is loaded in the background right after the first view (Back Hero USA,
   30/09/2026; owner: "đừng đợi tới rồi mới load" and "làm tốt nhất sao cho trên phone mọi thứ phải nhanh và hình cũng
   phải rõ… trên desktop mình thấy ổn r"). v2 (30/09 evening): WakaPaw's measured improvements ported in.
   Self-contained, no dependencies, loaded once from snippets/bh-head.liquid with <script src async>.

   PHONES / TABLETS (CFG.loadAllWhen):
   1. Starts when the HTML is parsed (readyState "interactive"), not at DOMContentLoaded: DOMContentLoaded waits for
      every deferred / module script (Horizon has many), which on a phone CPU held the queue back seconds. Then it waits
      for the first view: the img[fetchpriority="high"] hero / main photo, else every eager image on the first screen,
      else 300 ms; at the latest startCap after the HTML is parsed.
   2. Then EVERY <img loading="lazy"> on the page is loaded in the background, nearest to the screen first, maxInFlight
      at a time. A hidden image (other page of a paged grid, closed accordion) takes the place of its nearest laid-out
      container plus a penalty, so it follows the photos around it instead of waiting for the footer.
      Each one is fetched through a detached new Image() with the same sizes / srcset (so the same file is picked), and
      only once that copy has loaded is the real <img> switched to loading="eager" — it then paints from cache.
      NEVER re-assign src / srcset / sizes on the real image: that "kick" left broken "?" images on the owner's iPhone.
   3. While the shopper scrolls (the page or a rail), the queue starts nothing new (the line goes to what is coming into view);
      ~300 ms after the scrolling stops it re-sorts by distance from the new position and carries on.
   4. Look-ahead: an IntersectionObserver ~1400px ahead switches whatever the shopper is about to reach to eager at
      once. A rail / paged grid coming near: the cards in view (plus half a view either side) at once, the rest to the
      front of the queue (loading all 20 cards at once starved the two on screen). Swiping a rail loads the cards
      within one view either side of where it now is.
   DESKTOP (everything else): the look-ahead starts at DOMContentLoaded as in v1 (a rail loads all its cards once near).
   The background queue also runs there now (owner 30/09: "tất cả hình hay video cho các trang giờ chạy trước hết"),
   but only after the window load event + an idle moment: started at the hero it delayed desktop LCP ~0.4 s.
   When the queue has emptied, 'bh:images-done' is dispatched on window: assets/bh.js then starts buffering the short
   videos, so images always go first.
   Horizon scrolls .page-wrapper instead of the window from 990px: the observer uses it as its root there.
   Images inside drawers / dialogs (cart, menu, search, photo viewer) are skipped — they load when opened. Images added
   later (recommendations, apps, theme editor) join through a MutationObserver. */
(() => {
  const CFG = {
    maxInFlight: 5,
    // where the background queue runs; elsewhere only the look-ahead
    loadAllWhen: '(max-width: 1024px), (pointer: coarse)',
    lookAhead: '1400px 0px',
    noHeroDelay: 300,
    startCap: 8000, // phones: ms after the HTML is parsed
    deskStartCap: 4000, // desktop: ms after DOMContentLoaded (v1)
    deskQueueCap: 8000, // desktop: the background queue starts at window load + idle, at the latest this long after start
    stuckMs: 8000,
    pauseMs: 300,
    // containers whose images load together once the container comes near (rails, paged grids, thumb strips)
    groups: '[data-rail], [data-pgrid], .bh-gallery__thumbs',
    skip: 'cart-drawer-component, dialog, [role="dialog"], .menu-drawer, header-drawer, predictive-search-component, .shopify-section-group-header-group, .bh-lb',
    scrollerQuery: '(min-width: 990px)',
    scrollerSel: '.page-wrapper',
  };
  if (window.__imgLoader) return; window.__imgLoader = true;

  const mq = matchMedia(CFG.scrollerQuery);
  const loadAll = matchMedia(CFG.loadAllWhen).matches;
  const scroller = () => {
    const pw = mq.matches && document.querySelector(CFG.scrollerSel);
    return pw && /(auto|scroll)/.test(getComputedStyle(pw).overflowY) ? pw : null;
  };
  const view = () => { const sc = scroller(); return sc ? { top: sc.getBoundingClientRect().top, h: sc.clientHeight } : { top: 0, h: innerHeight }; };
  const lazyIn = (root) => [...(root.querySelectorAll ? root.querySelectorAll('img[loading="lazy"]') : [])].filter((i) => !i.closest(CFG.skip));
  const hasBox = (el) => { const r = el.getBoundingClientRect(); return r.width > 0 || r.height > 0; };

  // phones: lazy -> eager, the attribute alone (src / srcset / sizes untouched)
  const eager = (img) => { if (img.getAttribute('loading') === 'lazy') img.setAttribute('loading', 'eager'); };
  // desktop (v1, unchanged): lazy -> eager plus a detached copy that warms the cache
  function force(img) {
    if (img.getAttribute('loading') !== 'lazy') return;
    img.setAttribute('loading', 'eager');
    if (!img.complete) {
      const pre = new Image();
      if (img.sizes) pre.sizes = img.sizes;
      if (img.getAttribute('srcset')) pre.srcset = img.getAttribute('srcset');
      if (img.getAttribute('src')) pre.src = img.getAttribute('src');
      img.__pre = pre;
    }
  }
  // queue (phones): fetch the same file through a detached copy first, then switch the real img to eager (from cache)
  function warm(img) {
    return new Promise((res) => {
      let done = false;
      const fin = () => { if (done) return; done = true; clearTimeout(t); eager(img); res(); };
      const t = setTimeout(fin, CFG.stuckMs);
      if (img.getAttribute('loading') !== 'lazy' || (img.complete && img.naturalWidth)) return fin();
      const pre = new Image();
      pre.onload = fin; pre.onerror = fin;
      img.addEventListener('load', fin, { once: true });
      if (img.sizes) pre.sizes = img.sizes;
      if (img.getAttribute('srcset')) pre.srcset = img.getAttribute('srcset');
      pre.src = img.currentSrc || img.getAttribute('src') || '';
    });
  }

  // ---- background queue (phones / tablets), nearest to the screen first; ties keep DOM order
  function distance(img) {
    let el = img, pen = 0, r = img.getBoundingClientRect();
    while (el && !r.width && !r.height) { el = el.parentElement; pen = 1200; r = el ? el.getBoundingClientRect() : r; }
    if (!r.width && !r.height) return 1e9;
    const v = view();
    return pen + (r.bottom < v.top ? v.top - r.bottom : Math.max(0, r.top - v.top - v.h));
  }
  const byDistance = (list) => list.map((i) => [distance(i), i]).sort((a, b) => a[0] - b[0]).map((x) => x[1]);
  let queue = [], inFlight = 0, running = false, queueOn = false, doneSent = false;
  function enqueue(imgs) {
    const fresh = imgs.filter((i) => !i.__imgq); if (!fresh.length) return;
    fresh.forEach((i) => { i.__imgq = 1; });
    queue = byDistance(queue.concat(fresh));
    pump();
  }
  let pauseUntil = 0, resumeT = 0;
  function resume() {
    resumeT = 0;
    const wait = pauseUntil - performance.now();
    if (wait > 0) { resumeT = setTimeout(resume, wait + 20); return; }
    queue = byDistance(queue);
    pump();
  }
  // Shopify's sizes="auto" polyfill for Safari parks src / srcset in data-auto-sizes-* until the first paint: wait for it
  const parked = (img) => img.hasAttribute('data-auto-sizes-src') || img.hasAttribute('data-auto-sizes-srcset');
  let retryT = 0;
  function pump() {
    if (!running || performance.now() < pauseUntil) return;
    const later = [];
    while (inFlight < CFG.maxInFlight && queue.length) {
      const img = queue.shift();
      if (!img.isConnected || img.getAttribute('loading') !== 'lazy') continue; // gone, or the look-ahead already started it
      if (parked(img)) { later.push(img); continue; }
      inFlight++;
      warm(img).then(() => { inFlight--; pump(); });
    }
    if (later.length) { queue = later.concat(queue); if (!retryT) retryT = setTimeout(() => { retryT = 0; pump(); }, 400); }
    if (queueOn && !doneSent && !queue.length && !inFlight) { doneSent = true; window.dispatchEvent(new Event('bh:images-done')); }
  }
  // a rail / grid: the laid-out cards in view (and half a view either side) now, the rest to the front of the queue
  function loadGroup(g) {
    const imgs = lazyIn(g), gr = g.getBoundingClientRect(), pad = gr.width / 2;
    const near = [], rest = [];
    imgs.forEach((i) => { const r = i.getBoundingClientRect(); ((r.width || r.height) && r.right > gr.left - pad && r.left < gr.right + pad ? near : rest).push(i); });
    near.forEach(eager);
    const mid = gr.left + gr.width / 2;
    const key = (i) => { const r = i.getBoundingClientRect(); return r.width || r.height ? Math.abs(r.left + r.width / 2 - mid) : 1e9; };
    const front = rest.map((i) => [key(i), i]).sort((a, b) => a[0] - b[0]).map((x) => x[1]);
    front.forEach((i) => { i.__imgq = 1; });
    queue = front.concat(queue.filter((q) => front.indexOf(q) < 0));
    pump();
  }
  // a rail being swiped: the cards within one view either side of where it is now load at once
  function railAhead(sc) {
    if (sc.__imgRaf) return;
    sc.__imgRaf = requestAnimationFrame(() => {
      sc.__imgRaf = 0;
      const g = sc.getBoundingClientRect(), px = g.width, py = g.height;
      lazyIn(sc).forEach((i) => { const r = i.getBoundingClientRect(); if ((r.width || r.height) && r.right > g.left - px && r.left < g.right + px && r.bottom > g.top - py && r.top < g.bottom + py) eager(i); });
    });
  }
  // any scroll (page or rail, as WakaPaw) pauses the queue; a rail swiped sideways also loads its next cards at once
  function onScroll(e) {
    const t = e.target;
    if (t && t.nodeType === 1 && t !== document.documentElement && t !== document.body && t !== scroller() && !t.closest(CFG.skip)) railAhead(t);
    pauseUntil = performance.now() + CFG.pauseMs;
    if (!resumeT) resumeT = setTimeout(resume, CFG.pauseMs + 20);
  }

  // ---- look-ahead: whatever the shopper is about to reach jumps the queue
  let io = null;
  function observe(root) {
    if (!io) return;
    const q = root.querySelectorAll ? root : document;
    if (root.matches && root.matches(CFG.groups) && !root.closest(CFG.skip)) io.observe(root);
    q.querySelectorAll(CFG.groups).forEach((g) => { if (!g.closest(CFG.skip)) io.observe(g); });
    lazyIn(q).forEach((i) => { if (!i.closest(CFG.groups)) io.observe(i); });
  }
  function makeObserver() {
    if (!('IntersectionObserver' in window)) return;
    if (io) io.disconnect();
    io = new IntersectionObserver((es) => es.forEach((en) => {
      if (!en.isIntersecting) return;
      const t = en.target; io.unobserve(t);
      if (!loadAll) { if (t.tagName === 'IMG') force(t); else lazyIn(t).forEach(force); return; } // desktop: v1
      if (t.tagName === 'IMG') eager(t); else loadGroup(t);
    }), { root: scroller(), rootMargin: CFG.lookAhead });
    observe(document);
  }

  function startQueue() {
    if (queueOn) return; queueOn = true;
    window.__imgQueueStart = Math.round(performance.now()); // read by the before/after measurements only
    document.addEventListener('scroll', onScroll, { passive: true, capture: true });
    enqueue(lazyIn(document));
    pump(); // a page with nothing left to load still sends 'bh:images-done'
  }
  // desktop: the queue waits for the window load event + an idle moment, so it never competes with the first view
  function deskQueueLater() {
    const go = () => { if (window.requestIdleCallback) requestIdleCallback(startQueue, { timeout: 1500 }); else setTimeout(startQueue, 200); };
    if (document.readyState === 'complete') go(); else addEventListener('load', go, { once: true });
    setTimeout(startQueue, CFG.deskQueueCap);
  }
  function start() {
    if (running) return; running = true;
    window.__imgLoaderStart = Math.round(performance.now()); // read by the before/after measurements only
    makeObserver();
    if (mq.addEventListener) mq.addEventListener('change', makeObserver); else if (mq.addListener) mq.addListener(makeObserver);
    if (loadAll) startQueue(); else deskQueueLater();
    // images added later: recommendations, app widgets, theme editor re-renders
    let pending = [], scheduled = false;
    new MutationObserver((ms) => {
      for (const m of ms) for (const n of m.addedNodes) if (n.nodeType === 1 && n.tagName !== 'SCRIPT' && n.tagName !== 'LINK') pending.push(n);
      if (!pending.length || scheduled) return;
      scheduled = true;
      requestAnimationFrame(() => {
        scheduled = false;
        const nodes = pending; pending = [];
        const imgs = [];
        nodes.forEach((n) => { if (!n.isConnected) return; observe(n); if (n.tagName === 'IMG' && n.getAttribute('loading') === 'lazy' && !n.closest(CFG.skip)) imgs.push(n); imgs.push(...lazyIn(n)); });
        if (queueOn) enqueue(imgs);
      });
    }).observe(document.body, { childList: true, subtree: true });
  }

  // ---- first view first
  const settled = (img) => new Promise((res) => {
    if (img.complete) return res();
    const done = () => { clearTimeout(t); res(); };
    const t = setTimeout(done, CFG.stuckMs);
    img.addEventListener('load', done, { once: true }); img.addEventListener('error', done, { once: true });
  });
  function phoneFirstView() {
    setTimeout(start, CFG.startCap);
    let first = [...document.querySelectorAll('img[fetchpriority="high"]')].filter((i) => !i.closest(CFG.skip) && hasBox(i));
    if (!first.length) {
      const v = view();
      first = [...document.images].filter((i) => {
        if (i.closest(CFG.skip) || i.getAttribute('loading') === 'lazy') return false;
        const r = i.getBoundingClientRect();
        return r.width > 30 && r.height > 30 && r.bottom > v.top && r.top < v.top + v.h && r.right > 0 && r.left < innerWidth;
      });
    }
    const waiting = first.filter((i) => !i.complete);
    // first view already loaded: start now, synchronously — a setTimeout(0) here would wait until every deferred /
    // module script has run (~0.5-1 s on a phone CPU), which is exactly what starting at "interactive" is meant to skip
    if (!waiting.length) { if (first.length) start(); else setTimeout(start, CFG.noHeroDelay); return; }
    Promise.all(waiting.map(settled)).then(start);
  }
  function deskFirstView() { // v1, unchanged
    const first = document.querySelector('img[fetchpriority="high"]');
    if (!first) setTimeout(start, CFG.noHeroDelay);
    else if (first.complete) start();
    else { first.addEventListener('load', start, { once: true }); first.addEventListener('error', start, { once: true }); }
    setTimeout(start, CFG.deskStartCap);
  }
  if (loadAll) {
    if (document.readyState !== 'loading') phoneFirstView();
    else document.addEventListener('readystatechange', function h() { if (document.readyState === 'loading') return; document.removeEventListener('readystatechange', h); phoneFirstView(); });
  } else {
    // desktop: never before DOMContentLoaded, as v1 (loaded defer). This async file can run after the HTML is parsed
    // but before DOMContentLoaded (readyState is "interactive" in both), so ask the navigation timing which it is.
    const nav = performance.getEntriesByType ? performance.getEntriesByType('navigation')[0] : null;
    const dclDone = document.readyState === 'complete' || (nav ? nav.domContentLoadedEventStart > 0 : document.readyState !== 'loading');
    if (dclDone) deskFirstView(); else document.addEventListener('DOMContentLoaded', deskFirstView, { once: true });
  }
})();
